import React from "react";
import styled from "styled-components";
import moment from "moment";
import { Link } from "react-router-dom";

import Button from "@mui/material/Button";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import SettingsIcon from "@mui/icons-material/Settings";
import RouteIcon from "@mui/icons-material/Route";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import PeopleIcon from "@mui/icons-material/People";

import Skeleton from "@mui/material/Skeleton";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import theme from "../../styles/theme";
import { getFlag } from "../../utils/helpers/flags";
import { addToCalendar } from "../../utils/helpers/calendar";

import { faYoutube } from "@fortawesome/free-brands-svg-icons";

const Line = styled.div`
  width: 100%;
  height: 5px;
  margin: auto;
  margin-bottom: 20px;
`;

const ResultsButton = styled.div`
  width: 100%;
  height: 70px;
  background-color: ${theme.colors.darkLight};
  position: absolute;
  left: 0;
  bottom: 0px;
  // bottom: -70px;

  transition: all 0.2s ease;
  z-index: 4;
`;

const ReminderButton = styled(ResultsButton)`
  background-color: rgba(0, 0, 0, 0.35);
  cursor: pointer;
  bottom: -70px;

  &:hover {
    background-color: rgba(0, 0, 0, 0.5);
  }
`;

const ScheduleItem = styled.div`
  height: 100%;
  width: 100%;
  box-shadow: #00000040 0px 2px 8px 0px;
  color: ${theme.colors.white};
  max-width: 420px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  //background-color: ${theme.colors.darkLight};
  background-color: #1b2234;

  &.finished {
    // opacity: 0.5;

    ${Line} {
      background-color: ${theme.colors.red};
    }

    // &:hover {
    //   ${ResultsButton} {
    //     bottom: 0px;
    //   }
    // }
  }

  &.today {
    ${Line} {
      background-color: ${theme.colors.green};
    }

    ${ResultsButton} {
      background-color: #fe00326e;
    }
  }

  &.future {
    ${Line} {
      background-color: ${theme.colors.white};
    }
  }

  &:hover {
    ${ReminderButton} {
      bottom: 0px;
    }
  }

  @media (max-width: 768px) {
    ${ReminderButton} {
      bottom: 0px;
    }
  }

  ${(props) => !props.pointsRace && `background-color: #11251e;`}
`;

const ImageContainer = styled.div`
  width: 100%;
  aspect-ratio: 16 / 9;
  overflow: hidden;
  user-select: none;
`;

const ScheduleMainImage = styled.img`
  object-fit: cover;
  width: 100%;
  height: 100%;
  transition: transform 0.3s;
`;

const BottomContainer = styled.div`
  padding: 16px 12px;
  height: 220px;
`;

const TitleBlock = styled.div`
  flex-wrap: wrap;
  display: flex;
  align-items: center;
`;

const Flag = styled.img`
  width: 42px;
  height: 24px;
  object-fit: cover;
`;

const Title = styled.div`
  font-size: 21px;
  font-family: "Montserrat", sans-serif;
  font-weight: bold;
  margin-left: 8px;
`;

const Subtitle = styled.div`
  font-size: 14px;
  font-family: "Montserrat", sans-serif;
  color: ${theme.colors.white};
  opacity: 50%;
`;

const Track = styled.div`
  font-size: 14px;
  font-family: "Montserrat", sans-serif;
  color: ${theme.colors.white};
  display: flex;
  align-items: center;
  gap: 3px;
`;

const Date = styled.div`
  margin-top: 4px;
  font-size: 18px;
  font-family: "Montserrat", sans-serif;
  font-weight: 400;
  margin-left: auto;
  opacity: 50%;
  letter-spacing: -0.5px;
`;

const FinishedBlock = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  z-index: 2;
  background-color: rgb(0 0 0);
  opacity: 20%;
`;

const StyledLink = styled(Link)`
  text-decoration: none;
  padding: 0px 0px 20px 20px;
  color: ${theme.colors.dark};
  font-size: 16px;
  font-weight: 400;
  font-family: "Montserrat", sans-serif;
`;

const BlurredSpan = styled.span`
  filter: blur(6px);
`;

const BlurredTitle = styled(Title)`
  filter: blur(6px);
`;

const ResultText = styled.div`
  color: ${theme.colors.white};
  font-size: 16px;
  font-weight: 600;
  text-align: center;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: "Montserrat", sans-serif;
`;

export const ScheduleItemComponent = ({
  pointsRace,
  seasonId,
  id,
  name,
  hasResults,
  shortName,
  flag,
  image = "https://cdn.slickracing.com.ua/schedule/temp/blured_race.png",
  //image="https://ir-core-sites.iracing.com/members/member_images/cars/carid_205/shot_ss_5_screen_full.jpg",
  subtitle,
  date,
  car,
  configuration,
  teamRace,
}) => {
  const dateObj = moment(date, "YYYY-MM-DD");
  const dateString = dateObj.format("ddd DD.MM");

  const today = dateObj.format("YYYY-MM-DD") === moment().format("YYYY-MM-DD");
  const isFinished = !today && dateObj < moment();
  const isFuture = !today && !isFinished;
  const className = today ? "today" : isFinished ? "finished" : "future";

  const handleAddReminder = () => {
    addToCalendar({
      title: shortName || name,
      description: `${subtitle} \nCar: ${car} \nTrack: ${name} - ${configuration}`,
      location: `iRacing`,
      date,
    });
  };

  return (
    <ScheduleItem className={className} pointsRace={pointsRace}>
      <ImageContainer>
        <ScheduleMainImage src={image} />
      </ImageContainer>
      {isFinished && <FinishedBlock />}
      <BottomContainer>
        <Line />
        <TitleBlock>
          {flag && <Flag src={getFlag(flag)} />}
          {shortName ? <Title>{shortName}</Title> : <BlurredTitle>Track Name</BlurredTitle>}
          <Date>{dateString}</Date>
        </TitleBlock>
        <Subtitle>{subtitle}</Subtitle>

        <div style={{ marginTop: 12, height: 80 }}>
          <Track>
            <DirectionsCarIcon sx={{ fontSize: 14, color: theme.colors.green }} />
            {car ? <span> {car}</span> : <BlurredSpan> Name of a car</BlurredSpan>}
          </Track>

          <Track>
            <RouteIcon sx={{ fontSize: 14, color: theme.colors.green }} />
            {name ? <span> {name}</span> : <BlurredSpan> Name of a track</BlurredSpan>}
          </Track>

          <Track>
            <SettingsIcon sx={{ fontSize: 14, color: theme.colors.green }} />
            {configuration ? (
              <span> {configuration}</span>
            ) : (
              <BlurredSpan> Name of a config</BlurredSpan>
            )}
          </Track>

          {teamRace && (
            <Track>
              <PeopleIcon sx={{ fontSize: 14, color: theme.colors.green }} />
              <span>Team race</span>
            </Track>
          )}
        </div>
      </BottomContainer>

      {!isFinished && pointsRace && today && (
        <StyledLink to="https://www.youtube.com/@slickracingukraine/streams" target="_blank">
          <ResultsButton>
            <ResultText>Трансляція</ResultText>
          </ResultsButton>
        </StyledLink>
      )}

      {isFinished && hasResults && (
        <StyledLink to={`/results?s=${seasonId}&r=${id}`}>
          <ResultsButton>
            <ResultText>Переглянути результати</ResultText>
          </ResultsButton>
          {/* <Button
            size="small"
            color="secondary"
            variant="contained"
            sx={{
              fontFamily: "Roboto Condensed",
              marginTop: "12px",
              zIndex: 2,
              opacity: "85%",
            }}
            endIcon={<ArrowRightIcon sx={{}} />}
          >
            Результати
          </Button> */}
        </StyledLink>
      )}

      {isFuture && (
        <ReminderButton onClick={handleAddReminder}>
          <ResultText>Додати нагадування</ResultText>
        </ReminderButton>
      )}
    </ScheduleItem>
  );
};

export const ScheduleItemSkeleton = () => {
  return (
    <ScheduleItem>
      <ImageContainer>
        <Skeleton animation="wave" variant="rectangular" width="100%" height="100%" />
      </ImageContainer>
      <BottomContainer>
        <Skeleton width="60%" />
        <Skeleton />
        <Skeleton width="90%" />
        <Skeleton width="60%" />
        <Skeleton width="40%" />
        <Skeleton width="30%" />
        <Skeleton width="70%" />
        <Skeleton width="50%" />
        <Skeleton width="30%" />
      </BottomContainer>
    </ScheduleItem>
  );
};
